import { searchManagers } from 'shared/api/managers';
import { searchComplexes } from 'shared/api/complexes';
import { searchBuildings } from 'shared/api/buildings';
import { searchTenants } from 'shared/api/tenants';
import { getTenantPath } from 'shared/routes/tenant';
import { getBuildingPath, getComplexPath, getManagerPath } from 'shared/routes/building';
import { FILE_SIZES } from 'shared/constants';

export const prodMode = process.env.NODE_ENV === 'production';

export function bitsToString(bits) {
  let converted;
  let unit;

  converted = bits / (1024 * 1024);
  unit = 'Mb/s';

  if (converted >= 1000) {
    converted /= 1024;
    unit = 'Gb/s';
  }

  converted = Number.parseFloat(converted).toPrecision(2);

  return `${converted} ${unit}`;
}

export function FileSizeToString(bytes) {
  if (bytes >= FILE_SIZES.MB) {
    return `${(bytes / FILE_SIZES.MB).toFixed(1)} MB`;
  }
  return `${(bytes / FILE_SIZES.KB).toFixed(1)} KB`;
}

// Returns two values
//
// formatted: A string formatted to be a dollar amount, like $19.99
// value:     An integer that represents the price in cents, like 1999
//            This follows how Stripe represents currency: https://stripe.com/docs/currencies
export function getPrice(priceString) {
  if (!priceString) return { formatted: '', value: 0 };

  // Remove everything that's not a number or a . and then cast to a number.
  const decimalAmount = Number(priceString.replace(/[^0-9.]+/g, ''));

  const formatted = decimalAmount.toLocaleString('en-US', {
    maximumFractionDigits: 2,
  });

  const value = Math.round(decimalAmount * 100);

  return { formatted, value };
}

// Given a price value in cents, like 1999, returns $19.99
export function priceToString(price) {
  return `${(price / 100).toFixed(2)}`;
}

// Used for formatting dates for client displays
export function dateString(d) {
  if (!d) {
    return 'N/A';
  }
  return new Date(d).toLocaleDateString('en', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

// Used for formatting dates that are send to backend systems for date comparison searches
// where the required comparison format is yyyy-MM-dd
export function dateToISOString(d) {
  if (!d) {
    return 'N/A';
  }
  return new Date(d).toISOString().split('T', 1)[0];
}

export function dateTimeString(dateToFormat) {
  return new Date(dateToFormat).toLocaleTimeString([], {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export const objectsEqual = (o1, o2) => Object.keys(o1).length === Object.keys(o2).length
  && Object.keys(o1).every((p) => o1[p] === o2[p]);

export const compareArrays = (array1, array2) => array1.length === array2.length
  && array1.every((element, index) => element === array2[index]);

export const compareArrayOfObjects = (array1, array2) => array1.length === array2.length
  && array1.every((element, index) => objectsEqual(element, array2[index]));

export const differenceArrayOfObjects = (array1, array2) => array1.filter(
  (element1) => !array2.some((element2) => objectsEqual(element1, element2)),
).concat(array2.filter(
  (element2) => !array1.some((element1) => objectsEqual(element1, element2)),
));

export function formatPhoneNumber(phone) {
  if (!phone) return '';
  const phoneString = phone.toString();

  const numbers = phoneString.replace(/\D/g, '');

  const match = numbers.match(/^(\d)?(\d{3})(\d{3})(\d{4})$/);

  if (match) {
    let countryCode = '';
    if (match[1] && match[1] !== '1') {
      countryCode = `${match[1]} `;
    }
    return `${countryCode}(${match[2]}) ${match[3]}-${match[4]}`;
  }

  return phoneString;
}

export function formatEmployeeName(employee) {
  if (!employee.fullname) {
    if (!employee.employeeId) {
      return 'N/A';
    }
    return employee.employeeId.toString();
  }
  return `${employee.fullname}`;
}

export function copyValueToClipboard(value) {
  return navigator.clipboard.writeText(value);
}

export function copyLinkToClipboard(location, text) {
  const clipboardItem = new window.ClipboardItem({
    'text/html': new Blob([`<a href="${location}">${text}</a>`], { type: 'text/html' }),
    'text/plain': new Blob([text], { type: 'text/plain' }),
  });
  return navigator.clipboard.write([clipboardItem]);
}

// Adds additional search params from extra to current.
export function addSearchParams(current, extra) {
  const currentParams = new URLSearchParams(current);
  const extraParams = new URLSearchParams(extra);

  const iter = extraParams.keys();
  let curr = iter.next();
  while (!curr.done) {
    const key = curr.value;
    currentParams.set(key, extraParams.get(key));
    curr = iter.next();
  }

  return currentParams;
}

// Removes keys from a search string.
export function stripSearchString(search, ignoredKeys) {
  const strippedSearch = new URLSearchParams(search);
  if (ignoredKeys) ignoredKeys.forEach((key) => strippedSearch.delete(key));
  return strippedSearch;
}

export function capitalize(string) {
  return string ? string[0].toUpperCase() + string.slice(1) : string;
}

export function truncateString(string, maxLength) {
  if (string.length > maxLength) {
    return `${string.substring(0, maxLength)} ...`;
  }
  return string;
}

// Returns element in lst at i + delta.
// When i + delta not in list range, wraps to beginning.
export function wrapList(lst, i, delta) {
  const next = (i + delta) % lst.length;
  return lst[next >= 0 ? next : lst.length - 1];
}

export function isValidEmail(string) {
  return (/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,})+$/.test(string));
}

export function isValidICSEmail(string) {
  return !!string && (/^\w+@ics-llc.net+$/.test(string));
}

export function isValidPhone(string) {
  return (/^\d{10}$/.test(string.replace(/[^a-zA-Z0-9_.]+/g, '')));
}

export function isIsoDate(str) {
  if (!/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/.test(str)) return false;
  const d = new Date(str);
  return d instanceof Date && !Number.isNaN(d.getTime()) && d.toISOString() === str;
}

export function isBlankString(string) {
  if (string) {
    return (string.trim() === '');
  }
  return true;
}

export function isBooleanString(string) {
  return (string === 'true' || string === 'false ');
}

//
// Link formatting functions.
//

// Gets a link to the stripe object.
// Model is something like 'subscriptions', 'coupons', 'customers', etc.
export const getStripeUrl = prodMode
  ? (model, id) => `https://dashboard.stripe.com/${model}/${id}`
  : (model, id) => `https://dashboard.stripe.com/test/${model}/${id}`;

// Links to search page with query.
export const getStripeDataUrl = prodMode
  ? (q) => `https://dashboard.stripe.com/search?query=${q}`
  : (q) => `https://dashboard.stripe.com/test/search?query=${q}`;

export const modelMap = {
  manager: {
    placeholder: 'Search Managers...',
    searchService: searchManagers,
    title: 'Property Manager',
    plural: 'Managers',
    nameKey: 'propertyManagerName',
    idKey: 'propertyManagerId',
    parents: [],
    getView: getManagerPath,
  },
  complex: {
    placeholder: 'Search Complexes...',
    searchService: searchComplexes,
    title: 'Complex',
    plural: 'Complexes',
    nameKey: 'propertyComplexName',
    idKey: 'propertyComplexId',
    parents: ['manager'],
    getView: getComplexPath,
  },
  property: {
    title: 'Building',
    plural: 'Buildings',
    searchService: searchBuildings,
    nameKey: 'propertyName',
    idKey: 'propertyId',
    parents: ['complex', 'manager'],
    getView: getBuildingPath,
  },
  unit: {
    title: 'Unit',
    plural: 'Units',
  },
  tenant: {
    placeholder: 'Search Tenants...',
    searchService: searchTenants,
    getView: getTenantPath,
    title: 'Tenant',
    plural: 'Tenants',
  },
};

// Default params for every key not already in `search`. Pairs with a filter's defaultValue.
export function defaultSearch(search, kvPairs) {
  let compiledParams = '';
  Object.entries(kvPairs).forEach((pair) => {
    const [key, value] = pair;
    const currentValue = new URLSearchParams(search).get(key);
    if (!currentValue) {
      compiledParams = compiledParams === '' ? `${key}=${value}` : `${compiledParams}&${key}=${value}`;
    }
  });
  return compiledParams;
}

export function processRoutes(enabledRoutes, staticRoutes, navRoutes, savedFilters) {
  if (savedFilters) {
    enabledRoutes.forEach((r) => {
      const routeComponent = r;
      const componentKey = r.key || '';
      const staticRouteType = r.path !== undefined;
      const result = savedFilters.filters.find(
        ([key]) => key === componentKey,
      );
      if (result) {
        const [, resultValue] = result;
        if (r.getPath) {
          routeComponent.filters = `?${resultValue}`;
          navRoutes.push(routeComponent);
        } else if (r.path && !routeComponent.path.includes(resultValue)) {
          routeComponent.filters = `?${resultValue}`;
          staticRoutes.push(routeComponent);
        }
      } else if (!result && routeComponent.filters) {
        routeComponent.filters = '';
        (staticRouteType ? staticRoutes : navRoutes).push(r);
      } else (staticRouteType ? staticRoutes : navRoutes).push(r);
    });
  }
  return { staticRoutes, navRoutes };
}
